import { useEffect, useId, useRef, useState } from 'react';

/**
 * The design's own dropdown, in place of the browser's.
 *
 * ⚠️ **Not a `<select>`, and that is the design's call rather than a
 * preference.** A native select paints its list with the operating system's
 * chrome: white on macOS whatever the page is, its own font, its own radius. On
 * a dark admin it is the one control that does not belong to the design, and
 * there is no CSS that reaches inside it.
 *
 * What that costs is everything a native select gives for free, and it has to
 * be paid back by hand: a name, a role, the arrow keys, Escape, closing when
 * the pointer goes elsewhere. This is the APG **select-only combobox**, and it
 * is one pattern rather than two halves of two:
 *
 * - `role="combobox"` on the trigger, with `aria-controls` naming the list
 * - focus stays on the trigger; `aria-activedescendant` says which row the
 *   keyboard is on
 * - the list is a `listbox`, each row an `option` saying whether it is chosen
 *
 * ⚠️ **`aria-controls` is required, not decoration.** ARIA says the element
 * carrying `aria-activedescendant` must either contain that descendant or point
 * at it. The list is a sibling of the trigger, so without this the reference
 * does not resolve and arrowing through the options is announced as nothing at
 * all — silent, and only somebody using a screen reader ever meets it.
 *
 * ⚠️ **The label alone names it, and the contents are the *value*.** This looks
 * like the mistake the website's language picker made — an `aria-labelledby`
 * that hides what is selected — and it is the opposite, because of the role.
 * On a plain `<button>`, contents *are* the name, so naming it externally loses
 * the value; on `role="combobox"` implemented over a non-input element, ARIA
 * takes the text content as the combobox's **value**, announced after the name.
 * So this reads "Provider, combobox, OpenAI".
 *
 * The version before this named the trigger with both its label and its own id,
 * to force the value into the name. That produces "Provider OpenAI … OpenAI" in
 * a real browser and, in the accessibility-name library the tests use, resolves
 * to "Provider" anyway — a fix that was wrong in the browser and looked wrong in
 * the harness for a different reason.
 */
export function Select({
  value,
  options,
  onChange,
  labelledBy,
  disabled = false,
}: {
  /**
   * ⚠️ A value that is not in `options` is shown as it stands, with no row
   * selected. Deliberate: the alternative is a control that silently disagrees
   * with what was stored. It cannot happen while these lists are constants, and
   * it can once they come from an API.
   */
  value: string;
  options: readonly string[];
  onChange: (value: string) => void;
  /** Id of the visible label above the control. */
  labelledBy: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  /*
   * Which row the keyboard is on, which is not the same as which row is
   * chosen: arrowing through a list should not change the value until Enter.
   * A native select on macOS behaves this way and one on Windows does not; the
   * pattern's own guidance is this one, and it is the one that lets somebody
   * change their mind with Escape.
   */
  const [at, setAt] = useState(0);
  const box = useRef<HTMLDivElement>(null);
  const id = useId();
  const triggerId = `${id}-trigger`;
  const listId = `${id}-list`;
  const rowId = (index: number) => `${id}-option-${index}`;

  const show = () => {
    /*
     * ⚠️ An empty list does not open. There would be nothing in it to read, and
     * `aria-activedescendant` would point at a row that does not exist — a
     * dangling reference rather than a visible bug. The lists here are constants
     * today; the models are meant to come from the chosen provider once there is
     * an API to ask, and then this is reachable.
     */
    if (options.length === 0) return;
    setAt(Math.max(0, options.indexOf(value)));
    setOpen(true);
  };

  const choose = (index: number) => {
    // Reads as a guard and is really the type system: indexed access is checked,
    // and `at` is clamped to the list on every path that sets it.
    const picked = options[index];
    if (picked !== undefined) onChange(picked);
    setOpen(false);
  };

  /*
   * Closing on a pointer landing elsewhere, and on focus leaving the control.
   * `pointerdown` rather than `click`: a click that lands on another control
   * should reach that control, and waiting for `click` to close means the first
   * press outside is spent on closing.
   */
  useEffect(() => {
    if (!open) return;
    const away = (event: PointerEvent) => {
      if (!box.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', away);
    return () => document.removeEventListener('pointerdown', away);
  }, [open]);

  /*
   * Keep the highlighted row in view. The list caps at 240px — about eight rows
   * — so a short list never scrolls and this does nothing. A list of models from
   * a provider will, and then arrowing past the eighth row moves a highlight
   * nobody can see.
   */
  useEffect(() => {
    if (!open) return;
    /*
     * ⚠️ Called optionally because jsdom implements neither this nor
     * `CSS.escape`, so the tests reach this line and a plain call throws inside
     * an effect — which surfaces as every open-the-list test failing for a
     * reason that has nothing to do with what they assert. `getElementById`
     * rather than a selector for the same reason.
     */
    document.getElementById(rowId(at))?.scrollIntoView?.({ block: 'nearest' });
  });

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (disabled) return;

    if (!open) {
      // The keys that open a native select, and only those: a letter typed at
      // a closed trigger should not silently change the value.
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
        event.preventDefault();
        show();
      }
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      setOpen(false);
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      choose(at);
      return;
    }
    if (event.key === 'Tab') {
      // Leave it closed behind you rather than hanging over the next field.
      setOpen(false);
      return;
    }

    const move: Record<string, number> = {
      ArrowDown: at + 1,
      ArrowUp: at - 1,
      Home: 0,
      End: options.length - 1,
    };
    const next = move[event.key];
    if (next === undefined) return;
    event.preventDefault();
    // Clamped rather than wrapped: a list this short reads as a list, and
    // arrowing off the end back to the top loses people.
    setAt(Math.min(options.length - 1, Math.max(0, next)));
  };

  return (
    <div ref={box} className="relative block w-full">
      <button
        type="button"
        id={triggerId}
        role="combobox"
        disabled={disabled}
        aria-haspopup="listbox"
        /*
          ⚠️ Only while the list exists. The `<ul>` is unmounted when closed, so
          an unconditional `aria-controls` names an id that is not in the
          document — the same dangling reference as the empty-list case, and
          what `aria-valid-attr-value` reports. APG's own example never meets
          this because it keeps the list in the DOM and hides it with CSS.
        */
        aria-controls={open ? listId : undefined}
        aria-expanded={open}
        aria-labelledby={labelledBy}
        aria-activedescendant={open ? rowId(at) : undefined}
        onClick={() => (open ? setOpen(false) : show())}
        onKeyDown={onKeyDown}
        onBlur={(event) => {
          if (!box.current?.contains(event.relatedTarget as Node)) setOpen(false);
        }}
        className="field focus-ring flex w-full cursor-pointer items-center gap-2 text-left disabled:cursor-default"
        style={{ borderColor: open ? 'var(--accent)' : 'var(--line)' }}
      >
        <span className="min-w-0 flex-1 truncate">{value}</span>
        <svg
          aria-hidden="true"
          width="9"
          height="9"
          viewBox="0 0 12 12"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="flex-none"
          style={{ color: 'var(--dim)' }}
        >
          <path d="M2.5 4.5L6 8l3.5-3.5" />
        </svg>
      </button>

      {open ? (
        <ul
          id={listId}
          role="listbox"
          aria-labelledby={labelledBy}
          className="absolute top-[calc(100%+5px)] right-0 left-0 z-[70] m-0 flex max-h-60 list-none flex-col gap-px overflow-y-auto p-1"
          style={{
            background: 'var(--panel)',
            border: '1px solid var(--line2)',
            borderRadius: '9px',
            boxShadow: '0 18px 40px -22px rgba(0, 0, 0, 0.55)',
          }}
        >
          {options.map((option, index) => {
            const chosen = option === value;
            return (
              <li
                // Indexed, because two rows spelled the same way are a data
                // problem and not a reason for React to lose track of them.
                key={`${option}-${index}`}
                id={rowId(index)}
                role="option"
                aria-selected={chosen}
                /*
                  ⚠️ `onMouseDown` with the default prevented, not `onClick`.
                  The trigger holds focus; a press here would blur it first,
                  `onBlur` would close the list, and the click would land on
                  whatever the list had been covering.
                */
                onMouseDown={(event) => {
                  event.preventDefault();
                  choose(index);
                }}
                onMouseEnter={() => setAt(index)}
                className="flex min-h-[30px] cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12.5px]"
                style={{
                  background: chosen || at === index ? 'var(--panel2)' : 'transparent',
                  color: chosen ? 'var(--text)' : 'var(--muted)',
                }}
              >
                <span className="min-w-0 flex-1">{option}</span>
                {/*
                  Always rendered, at zero opacity when it is not the one — so
                  the rows do not shift sideways as the tick moves between them.
                  `aria-hidden` because `aria-selected` on the row already says
                  it, and a screen reader reading "✓" after "selected" says it
                  twice.
                */}
                <span
                  aria-hidden="true"
                  className="text-[11px]"
                  style={{ color: 'var(--accent)', opacity: chosen ? 1 : 0 }}
                >
                  ✓
                </span>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
